import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AppDevelopmentSection = () => {
  const [activePrototype, setActivePrototype] = useState(0);

  const prototypes = [
    {
      title: 'E-commerce Mobile App',
      category: 'Retail',
      description: 'Complete shopping experience with cart, payments, and order tracking.',
      screens: ['Login', 'Product Catalog', 'Cart', 'Checkout', 'Order Status'],
      features: ['Push Notifications', 'Payment Integration', 'Wishlist', 'Reviews'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Fitness Tracking App',
      category: 'Health & Fitness',
      description: 'Comprehensive fitness tracking with workouts, nutrition, and progress monitoring.',
      screens: ['Dashboard', 'Workout Plans', 'Progress', 'Nutrition', 'Social'],
      features: ['Wearable Integration', 'Social Sharing', 'Custom Workouts', 'Analytics'],
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'Business Management App',
      category: 'Productivity',
      description: 'All-in-one business management with CRM, invoicing, and team collaboration.',
      screens: ['Dashboard', 'Contacts', 'Projects', 'Invoices', 'Reports'],
      features: ['Cloud Sync', 'Team Collaboration', 'Document Management', 'Reporting'],
      color: 'from-blue-500 to-purple-500'
    }
  ];

  const appTypes = [
    {
      title: 'Native iOS Apps',
      description: 'Built with Swift for optimal performance and iOS-specific features.',
      icon: 'Smartphone',
      technologies: ['Swift', 'SwiftUI', 'Core Data', 'CloudKit'],
      benefits: ['Best Performance', 'Native Features', 'App Store Optimized']
    },
    {
      title: 'Native Android Apps',
      description: 'Developed with Kotlin for seamless Android integration.',
      icon: 'Tablet',
      technologies: ['Kotlin', 'Jetpack Compose', 'Room', 'Firebase'],
      benefits: ['Material Design', 'Google Services', 'Play Store Ready']
    },
    {
      title: 'Cross-Platform Apps',
      description: 'Single codebase for both iOS and Android using React Native or Flutter.',
      icon: 'Globe',
      technologies: ['React Native', 'Flutter', 'Expo', 'Firebase'],
      benefits: ['Cost Effective', 'Faster Development', 'Unified Experience']
    }
  ];

  const developmentStats = [
    { label: 'Apps Delivered', value: '150+', icon: 'Smartphone' },
    { label: 'Average Rating', value: '4.8/5', icon: 'Star' },
    { label: 'User Retention', value: '85%', icon: 'Users' },
    { label: 'App Store Approvals', value: '98%', icon: 'CheckCircle' }
  ];

  const clientSuccess = [
    {
      client: 'FitLife Pro',
      industry: 'Fitness',
      challenge: 'Create engaging fitness tracking app',
      solution: 'Native iOS/Android apps with wearable integration',
      results: {
        downloads: '50K+',
        rating: '4.9/5',
        retention: '78%'
      },
      timeline: '5 months'
    },
    {
      client: 'ShopEasy',
      industry: 'E-commerce',
      challenge: 'Mobile-first shopping experience',
      solution: 'Cross-platform app with advanced features',
      results: {
        downloads: '100K+',
        rating: '4.7/5',
        retention: '82%'
      },
      timeline: '6 months'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Smartphone" size={16} />
            <span>App Development</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Mobile Apps That Users Love
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From concept to App Store, we create mobile applications that deliver exceptional user experiences and drive business growth across iOS and Android platforms.
          </p>
        </div>

        {/* Interactive Prototype Viewer */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Interactive App Prototypes
          </h3>
          
          <div className="bg-card rounded-2xl p-8 brand-shadow">
            {/* Prototype Selector */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {prototypes?.map((prototype, index) => (
                <button
                  key={index}
                  onClick={() => setActivePrototype(index)}
                  className={`px-6 py-3 rounded-lg font-semibold brand-transition ${
                    activePrototype === index
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {prototype?.title}
                </button>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* App Preview */}
              <div className="relative">
                <div className="w-80 h-[600px] mx-auto relative">
                  {/* Phone Frame */}
                  <div className="absolute inset-0 bg-gray-800 rounded-[3rem] p-2">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                      {/* Status Bar */}
                      <div className="h-12 bg-gray-50 flex items-center justify-between px-6 text-sm">
                        <span className="font-medium">9:41</span>
                        <div className="flex items-center space-x-1">
                          <Icon name="Signal" size={16} />
                          <Icon name="Wifi" size={16} />
                          <Icon name="Battery" size={16} />
                        </div>
                      </div>
                      
                      {/* App Content */}
                      <div className={`h-full bg-gradient-to-br ${prototypes?.[activePrototype]?.color} p-6 flex flex-col justify-center items-center text-white`}>
                        <Icon name="Smartphone" size={64} className="mb-4 opacity-80" />
                        <h4 className="text-xl font-bold mb-2">{prototypes?.[activePrototype]?.title}</h4>
                        <p className="text-center text-sm opacity-90 mb-6">{prototypes?.[activePrototype]?.description}</p>
                        
                        {/* Screen Flow */}
                        <div className="space-y-2 w-full">
                          {prototypes?.[activePrototype]?.screens?.map((screen, idx) => (
                            <div key={idx} className="bg-white/20 rounded-lg p-3 text-center">
                              <span className="text-sm font-medium">{screen}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* App Details */}
              <div className="space-y-6">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm font-medium">
                      {prototypes?.[activePrototype]?.category}
                    </span>
                  </div>
                  <h4 className="text-2xl font-bold text-foreground mb-4">
                    {prototypes?.[activePrototype]?.title}
                  </h4>
                  <p className="text-muted-foreground mb-6">
                    {prototypes?.[activePrototype]?.description}
                  </p>
                </div>

                <div>
                  <h5 className="font-semibold text-foreground mb-4">Key Features:</h5>
                  <div className="grid grid-cols-2 gap-3">
                    {prototypes?.[activePrototype]?.features?.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Icon name="Check" size={16} className="text-success" />
                        <span className="text-foreground text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-4">
                  <Button variant="outline" iconName="Play" iconPosition="left">
                    View Demo
                  </Button>
                  <Button variant="default" iconName="Download" iconPosition="left">
                    Download Case Study
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* App Development Types */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            Choose Your Development Approach
          </h3>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {appTypes?.map((type, index) => (
              <div key={index} className="bg-card rounded-2xl p-8 brand-shadow brand-hover-lift brand-transition">
                <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center mb-6">
                  <Icon name={type?.icon} size={32} className="text-accent-foreground" />
                </div>
                
                <h4 className="text-xl font-bold text-foreground mb-4">{type?.title}</h4>
                <p className="text-muted-foreground mb-6">{type?.description}</p>
                
                <div className="mb-6">
                  <h5 className="font-semibold text-foreground mb-3">Technologies:</h5>
                  <div className="flex flex-wrap gap-2">
                    {type?.technologies?.map((tech, idx) => (
                      <span key={idx} className="px-3 py-1 bg-surface text-foreground rounded-full text-sm">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h5 className="font-semibold text-foreground mb-3">Benefits:</h5>
                  <div className="space-y-2">
                    {type?.benefits?.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Icon name="Check" size={16} className="text-success" />
                        <span className="text-foreground text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Development Stats */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 lg:p-12 mb-16">
          <h3 className="text-2xl lg:text-3xl font-bold text-foreground text-center mb-12">
            Our App Development Track Record
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {developmentStats?.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-success rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat?.icon} size={32} className="text-success-foreground" />
                </div>
                <p className="text-3xl font-bold text-foreground mb-2">{stat?.value}</p>
                <p className="text-muted-foreground">{stat?.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Client Success Stories */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            App Success Stories
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {clientSuccess?.map((story, index) => (
              <div key={index} className="bg-card rounded-2xl p-8 brand-shadow">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-xl font-bold text-foreground">{story?.client}</h4>
                    <p className="text-muted-foreground">{story?.industry}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Timeline</p>
                    <p className="font-semibold text-foreground">{story?.timeline}</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Challenge:</p>
                  <p className="text-foreground mb-4">{story?.challenge}</p>
                  <p className="text-sm text-muted-foreground mb-2">Solution:</p>
                  <p className="text-foreground">{story?.solution}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-success">{story?.results?.downloads}</p>
                    <p className="text-xs text-muted-foreground">Downloads</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-accent">{story?.results?.rating}</p>
                    <p className="text-xs text-muted-foreground">Rating</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">{story?.results?.retention}</p>
                    <p className="text-xs text-muted-foreground">Retention</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Development Process */}
        <div className="bg-card rounded-2xl p-8 lg:p-12">
          <h3 className="text-2xl lg:text-3xl font-bold text-foreground text-center mb-12">
            Our App Development Process
          </h3>
          
          <div className="grid md:grid-cols-5 gap-8">
            {[
              { step: '01', title: 'Ideation', desc: 'Concept validation & market research', duration: '1-2 weeks' },
              { step: '02', title: 'Design', desc: 'UI/UX design & interactive prototypes', duration: '2-3 weeks' },
              { step: '03', title: 'Development', desc: 'Native or cross-platform development', duration: '6-12 weeks' },
              { step: '04', title: 'Testing', desc: 'QA testing & device compatibility', duration: '2-3 weeks' },
              { step: '05', title: 'Launch', desc: 'App store submission & marketing', duration: '1-2 weeks' }
            ]?.map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-success text-success-foreground rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                  {phase?.step}
                </div>
                <h4 className="font-bold text-foreground mb-2">{phase?.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">{phase?.desc}</p>
                <p className="text-xs text-accent font-medium">{phase?.duration}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button 
            variant="default" 
            className="bg-success hover:bg-success/90 text-success-foreground"
            iconName="Smartphone"
            iconPosition="left"
          >
            Start App Project
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AppDevelopmentSection;